import constants from '@Common/Core/Constant';

import { TimeSelectionDialog, TimeSelectionParams } from '@Common/Dialogs/TimeSelectionDialog';
import { TimeInterval } from '@Common/Enums';
import timeHelper, { dateTimeInterval } from '@Common/Helpers/TimeHelper';
import { DateTimeInterval } from '@InstanceFactory';
import formatHelper from '@Common/Helpers/FormatHelper';
import { Label } from './Label';
import { safeDestroys } from '@Common/Core/Destroyable';

export interface ITimeIntervalControl {
    showTime?: boolean;
    formatName?: string;
    width?: number;
    height?: number;
    timeInterval?: DateTimeInterval;
    css?: any;
    className?: string;
}

export class TimeIntervalControl {
    private readonly label = new Label({});

    private readonly image: JQuery = $('<div/>', {
        on: {
            click: this.openDialog.bind(this)
        }
    });

    private readonly layout: JQuery = $('<div />', {
        class: 'time-interval-control',
        html: [this.label.getControl(), this.image]
    });

    private changed: ICallback<DateTimeInterval> = _.noop;

    constructor(private param: ITimeIntervalControl) {
        this.param = _.defaults(this.param, {
            showTime: false,
            formatName: null,
            width: 330,
            height: 30,
            timeInterval: dateTimeInterval(constants.minDate, constants.maxDate),
            css: null,
            className: ''
        });

        this.layout.addClass(this.param.className).css({
            width: this.param.width,
            height: this.param.height
        });

        this.image.css({
            height: this.param.height
        });
        this.updateLabel();
    }

    public onChanged(fn: ICallback<DateTimeInterval>): void {
        if (_.isFunction(fn)) {
            this.changed = fn;
        }
    }

    public getControl(): JQuery {
        return this.layout;
    }

    public getValue(): DateTimeInterval {
        return this.param.timeInterval;
    }

    public setValue(interval: DateTimeInterval): DateTimeInterval {
        if (interval) {
            this.param.timeInterval = interval;
            this.updateLabel();
        }

        return this.param.timeInterval;
    }

    public setState(state: boolean): void {
        this.label.enabled(state);

        if (state) {
            this.image.removeClass('disabled');
        } else {
            this.image.addClass('disabled');
        }
    }

    public adjust(width: number, height: number): void {
        this.layout.css({ width, height });
    }

    private openDialog(): void {
        const interval = _.cloneDeep(this.param.timeInterval);

        let nearestStartTo;
        if (!this.param.showTime) {
            nearestStartTo = timeHelper.nearestStartTime(interval.to, TimeInterval.day1).valueOf();
            if (nearestStartTo === interval.to.valueOf()) {
                interval.to.setDate(interval.to.getDate() - 1);
            }
        }

        const dialog = new TimeSelectionDialog(
            new TimeSelectionParams({
                showTime: this.param.showTime,
                timeInterval: interval,
                showNoSelection: false
            })
        );

        dialog.onOk(value => {
            if (!this.param.showTime) {
                if (nearestStartTo === value.to.valueOf()) {
                    value.to.setDate(value.to.getDate() + 1);
                }
            }

            this.param.timeInterval = value;
            this.updateLabel();
            this.changed(this.param.timeInterval);
        });

        dialog.show();
    }

    private updateLabel(): void {
        this.label.setCaption(formatHelper.formatDateTimeInterval(this.param.timeInterval, this.param.formatName));
    }

    public destroy(): void {
        safeDestroys(this.label, this.layout);
    }
}
